import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import Button from '../../../components/ui/Button';
import { toast } from 'react-hot-toast';
// import { useGetOrderItemDetailsQuery } from '../../../redux/apis/endpoints/orderApi'; // No longer used directly
import { useGetFulfillmentGroupDetailsQuery } from '../../../redux/apis/endpoints/orderApi'; // Import new hook
import { useSubmitRequestMutation } from '../../../redux/apis';
import { orderApi } from '../../../redux/apis/endpoints/orderApi';
import FormInput from '../../../components/ui/FormInput'; // Import FormInput
import Dropdown from '../../../components/ui/Dropdown'; // Import Dropdown

// Map reasons to the format expected by Dropdown
const RETURN_REASONS_OPTIONS = [
  { value: '', label: 'Select a reason' }, // Add placeholder option
  { value: 'wrong_item', label: 'Wrong item received' },
  { value: 'damaged', label: 'Item arrived damaged' },
  { value: 'defective', label: 'Item is defective' },
  { value: 'not_as_described', label: 'Item not as described' },
  { value: 'size_fit_issue', label: 'Size/fit issues' },
  { value: 'changed_mind', label: 'Changed my mind' },
  { value: 'other', label: 'Other (please specify)' }
];

const ReturnRequestPage = () => {
  const { fulfillmentGroupId, orderItemComponentId } = useParams(); // Updated params
  const history = useHistory();
  const user = useSelector(state => state.auth.user);
  const dispatch = useDispatch();
  
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [comments, setComments] = useState('');
  const [quantity, setQuantity] = useState(1);
  
  const [orderItem, setOrderItem] = useState(null); // State to hold the found order item
  const [fulfillmentGroup, setFulfillmentGroup] = useState(null); // State to hold the fetched FG

  // Fetch Fulfillment Group details
  const { 
    data: fetchedFulfillmentGroup, 
    isLoading: isLoadingFgDetails, 
    error: fgDetailsError 
  } = useGetFulfillmentGroupDetailsQuery(
    fulfillmentGroupId, 
    { skip: !fulfillmentGroupId }
  );
  
  const [submitRequest, { isLoading: isSubmitting, error: submitRequestError }] = useSubmitRequestMutation();

  // Effect to find the order item once fulfillment group data is loaded
  useEffect(() => {
    if (fetchedFulfillmentGroup && orderItemComponentId) {
      setFulfillmentGroup(fetchedFulfillmentGroup); // Store the FG
      const item = fetchedFulfillmentGroup.orderItems?.find(
        it => it.id.toString() === orderItemComponentId
      );
      if (item) {
        setOrderItem(item);
        setQuantity(item.quantity || 1);
      } else {
        toast.error('Order item not found in this fulfillment group.');
        console.error(`Order item with component ID ${orderItemComponentId} not found in FG ${fulfillmentGroupId}`);
        // Optionally redirect or show a more permanent error
      }
    }
  }, [fetchedFulfillmentGroup, orderItemComponentId, fulfillmentGroupId]);

  const handleReasonChange = (selectedReasonValue) => { // For Dropdown, it directly passes the value
    setReason(selectedReasonValue);
    if (selectedReasonValue !== 'other') {
      setOtherReason('');
    }
  };

  const handleQuantityChange = (e) => {
    const val = parseInt(e.target.value, 10);
    // Ensure quantity is at least 1 and not more than itemQuantity
    setQuantity(Math.max(1, Math.min(val || 1, orderItem?.quantity || 1)));
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('You must be logged in to request a return');
      return;
    }
    
    if (!reason) {
      toast.error('Please select a reason for your return');
      return;
    }
    
    if (reason === 'other' && !otherReason.trim()) {
      toast.error('Please specify a reason for your return');
      return;
    }

    if (!orderItem || !fulfillmentGroup) {
        toast.error('Item or order details are missing. Cannot submit request.');
        return;
    }

    try {
      const requestPayload = {
        requestType: 'item_return',
        relatedOrderItemComponentId: orderItem.id.toString(),
        relatedFulfillmentGroupId: fulfillmentGroup.id,
        requestDetails: {
            reason: reason === 'other' ? otherReason.trim() : reason,
            quantity,
            comments: comments.trim(),
        },
        // user and merchant will be auto-added by backend based on FG/Order
      };
      
      // Submit the request and wait for it to complete
      await submitRequest(requestPayload).unwrap();
      toast.success('Return request submitted successfully!');
      
      // Force refresh data before navigation to ensure latest state is shown
      try {
        // Refetch fulfillment group details to get updated order item status
        await dispatch(orderApi.endpoints.getFulfillmentGroupDetails.initiate(fulfillmentGroup.id, { forceRefetch: true })).unwrap();
        // Refetch all orders to ensure transactions page has latest data
        await dispatch(orderApi.endpoints.getMyOrders.initiate(undefined, { forceRefetch: true })).unwrap();
        // Only navigate after data is refreshed
        history.push(`/dashboard/transactions/fg/${fulfillmentGroup.id}`);
      } catch (refreshError) {
        console.error('Error refreshing data:', refreshError);
        // Still navigate even if refresh fails
        history.push(`/dashboard/transactions/fg/${fulfillmentGroup.id}`);
      }
    } catch (err) {
      console.error('Failed to submit return request:', err);
      const errorMessage = err.data?.message || err.data?.error?.message || err.message || 'Failed to submit return request';
      toast.error(errorMessage);
    }
  };

  if (isLoadingFgDetails) {
    return <LoadingSpinner />;
  }

  if (fgDetailsError) {
    return (
      <div className="text-red-500 text-center p-4">
        Error loading order details: {fgDetailsError.message || JSON.stringify(fgDetailsError)}
      </div>
    );
  }

  if (!orderItem) { // Check if orderItem was found and set
    return (
      <div className="text-center p-4">
        {isLoadingFgDetails ? <LoadingSpinner /> : <p className="text-red-500">Order item details could not be loaded. It might not exist or there was an error.</p>}
      </div>
    );
  }

  // Extract item details for display
  const { product, price, quantity: itemQuantity } = orderItem;

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Request a Return</h1>
      
      <div className="bg-gray-50 p-4 rounded-md mb-6">
        <div className="flex items-start">
          {product?.photos?.[0]?.url && (
            <img 
              src={product.photos[0].url} 
              alt={product.name} 
              className="w-24 h-24 object-cover rounded-md mr-4"
            />
          )}
          <div>
            <h2 className="text-lg font-semibold">{product?.name || 'Product'}</h2>
            <p className="text-gray-600">Price: ${price?.toFixed(2) || 'N/A'}</p>
            <p className="text-gray-600">Quantity Originally Ordered: {itemQuantity || 'N/A'}</p>
            {/* {fulfillmentGroup && <p className="text-sm text-gray-500">Order ID: {fulfillmentGroup.order?.id || 'N/A'}</p>}
            {fulfillmentGroup && <p className="text-sm text-gray-500">Fulfillment Group ID: {fulfillmentGroup.id}</p>} */}
          </div>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex flex-col md:flex-row md:gap-4">
          <div className="flex-1 mb-6 md:mb-0">
            <Dropdown
              title="Reason for Return"
              options={RETURN_REASONS_OPTIONS}
              value={reason}
              onChange={handleReasonChange}
              required
              disabled={isSubmitting}
              placeholder="Select a reason"
              className="w-full" // Ensure dropdown takes full width of its container
            />
          </div>
          <div className="w-full md:w-1/3"> {/* Adjust width as needed */}
            <FormInput
              label="Quantity to Return"
              type="number"
              name="quantity" // Added name prop
              min="1"
              max={itemQuantity || 1}
              value={quantity}
              onChange={handleQuantityChange} // Use updated handler
              required
              disabled={isSubmitting || (itemQuantity === 1)}
              className="w-full" // Ensure input takes full width
            />
          </div>
        </div>
        
        {reason === 'other' && (
          <FormInput
            label="Please specify *"
            type="text"
            name="otherReason" // Added name prop
            value={otherReason}
            onChange={(e) => setOtherReason(e.target.value)}
            required
            disabled={isSubmitting}
            className="w-full" // Ensure input takes full width
          />
        )}
        
        <FormInput
          label="Additional Comments (Optional)"
          as="textarea"
          name="comments" // Added name prop
          rows="4"
          placeholder="Please provide any additional details about your return request..."
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          disabled={isSubmitting}
          className="w-full" // Ensure input takes full width
        />
        
        <div className="flex items-center justify-end">
          <Button
            variant="secondary"
            className="mr-2"
            onClick={() => history.goBack()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || !orderItem} // Disable if orderItem not found
            isLoading={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Return Request'}
          </Button>
        </div>
        
        {submitRequestError && (
          <div className="text-red-500 text-sm mt-2">
            Error: {submitRequestError.data?.message || submitRequestError.data?.error?.message || submitRequestError.message || 'An unexpected error occurred'}
          </div>
        )}
      </form>
    </div>
  );
};

export default ReturnRequestPage; 